import React, { cloneElement } from "react";
import { connect } from "react-redux";
import { Redirect, withRouter } from "react-router-dom";
import { compose } from "redux";
import * as style from "./styles.module.scss";
import history from "../../history";

import OnboardStateItem from "../../components/OnboardStateItem";
import {
  hasContractForServices,
  isCvCompleted,
  isDemographicsCompleted,
  isEmergencyContactCompleted,
  isHealthAndDisabilityCompleted,
  isPersonalDetailsCompleted,
  isProfilePictureCompleted,
  isRTWCompleted,
  isTypeOfWorkIsCompleted,
  isWorkHistoryCompleted,
  isWorkingWeekOptOutCompleted,
  returnFirstNonCompletedStep,
} from "../../helpers/candidateJob";

const renderSectionsWithForms = (sections, jobId) =>
  Object.entries(sections).map(([sectionUrl, sectionDetails]) => {
    const { forms, completed, sectionName } = sectionDetails;
    return {
      urls: forms.map(
        (form) =>
          `/my-account/profile/edit/job/${jobId}/${sectionUrl}/${form.url}`
      ),
      content: <span>{sectionName}</span>,
      completed,
    };
  });

const updateFirstNonCompletedStep = (data, targetUrl) => {
  const updatedData = { ...data };

  Object.entries(data).some(([sectionKey, section]) => {
    const updatedSteps = { ...section.steps };

    const isUpdated = Object.entries(section.steps).some(([stepKey, step]) => {
      if (step.url === targetUrl) {
        updatedSteps[stepKey] = {
          ...step,
          firstNonCompletedStep: true,
        };
        return true;
      }
      return false;
    });

    if (isUpdated) {
      updatedData[sectionKey] = {
        ...section,
        steps: updatedSteps,
      };
      return true;
    }

    return false;
  });

  return updatedData;
};
const CandidateJobContainer = ({
  children,
  appData,
  jobId,
  formId,
  section,
}) => {
  const { candidateJobs } = appData;

  const currentJob = candidateJobs.find((job) => job.id === jobId);
  const customSectionForms = currentJob?.sections[section]?.forms || [];

  const customForm = customSectionForms.find((form) => form.url === formId);

  if (!currentJob || (currentJob && formId && section && !customForm)) {
    return <Redirect to="/profile" />;
  }

  const stepsUrls = {
    personalDetails: `/my-account/profile/edit/job/${jobId}/registration/personal-details`,
    profilePicture: `/my-account/profile/edit/job/${jobId}/registration/profile-picture`,
    typeOfWork: `/my-account/profile/edit/job/${jobId}/registration/type-of-work`,
    cv: `/my-account/profile/edit/job/${jobId}/registration/cv`,
    workHistory: `/my-account/profile/edit/job/${jobId}/registration/work-history`,
    emergencyContact: `/my-account/profile/edit/job/${jobId}/registration/emergency-contact`,
    demographics: `/my-account/profile/edit/job/${jobId}/registration/demographics`,
    wtr48HrWaiver: `/my-account/profile/edit/job/${jobId}/legal-compliance/48-hour-opt-out`,
    healthAndDisability: `/my-account/profile/edit/job/${jobId}/legal-compliance/health-and-disability`,
    contractForServices: `/my-account/profile/edit/job/${jobId}/legal-compliance/contract-for-services`,
    rtw: `/my-account/profile/edit/job/${jobId}/legal-compliance/rtw`,
    yotiSuccess: `/my-account/profile/edit/job/${jobId}/legal-compliance/rtw/yoti/success`,
    yotiError: `/my-account/profile/edit/job/${jobId}/legal-compliance/rtw/yoti/error`,
  };

  const {
    personalDetails,
    profilePicture,
    typeOfWork,
    cv,
    workHistory,
    emergencyContact,
    demographics,
    wtr48HrWaiver,
    healthAndDisability,
    contractForServices,
    rtw,
    yotiSuccess,
    yotiError,
  } = stepsUrls;

  const buildRegistrationState = () => {
    const personalDetailsCompleted = isPersonalDetailsCompleted(appData);
    const profilePictureCompleted = isProfilePictureCompleted(appData);
    const workHistoryCompleted =
      isWorkHistoryCompleted(appData) || isCvCompleted(appData);
    const typeOfWorkCompleted = isTypeOfWorkIsCompleted(appData);
    const emergencyContactCompleted = isEmergencyContactCompleted(appData);
    const demographicsCompleted = isDemographicsCompleted(appData);

    const steps = {
      "Personal details": {
        completed: personalDetailsCompleted,
        url: personalDetails,
        firstNonCompletedStep: false,
      },
      "Profile picture": {
        completed: profilePictureCompleted,
        url: profilePicture,
        firstNonCompletedStep: false,
      },
      "Type of work": {
        completed: typeOfWorkCompleted,
        url: typeOfWork,
        firstNonCompletedStep: false,
      },
      "CV/work history": {
        completed: workHistoryCompleted,
        url: cv,
        firstNonCompletedStep: false,
      },
      "Emergency contact": {
        completed: emergencyContactCompleted,
        url: emergencyContact,
        firstNonCompletedStep: false,
      },
      Demographics: {
        completed: demographicsCompleted,
        url: demographics,
        firstNonCompletedStep: false,
      },
    };

    const totalStepsCompleted = Object.values(steps).filter(Boolean).length;

    return {
      "Section 1: Registration": {
        steps,
        totalSteps: Object.keys(steps).length,
        totalStepsCompleted,
      },
    };
  };

  const buildLegalComplianceState = () => {
    const workingWeekOptOutCompleted = isWorkingWeekOptOutCompleted(appData);
    const healthAndDisabilityCompleted =
      isHealthAndDisabilityCompleted(appData);
    const contractForServicesCompleted = hasContractForServices(appData);
    const rtwCompleted = isRTWCompleted(appData);

    const steps = {
      "48 hour working week opt in/opt out": {
        completed: workingWeekOptOutCompleted,
        url: wtr48HrWaiver,
        firstNonCompletedStep: false,
      },
      "Health & disability question": {
        completed: healthAndDisabilityCompleted,
        url: healthAndDisability,
        firstNonCompletedStep: false,
      },
      "Contract for services": {
        completed: contractForServicesCompleted,
        url: contractForServices,
        firstNonCompletedStep: false,
      },
      "Right to work": {
        completed: rtwCompleted,
        url: rtw,
        firstNonCompletedStep: false,
      },
    };

    const totalStepsCompleted = Object.values(steps).filter(Boolean).length;

    return {
      "Section 2: Legal compliance": {
        steps,
        totalSteps: Object.keys(steps).length,
        totalStepsCompleted,
      },
    };
  };

  const buildCustomOnboardingState = () =>
    Object.entries(currentJob.sections).reduce(
      (acc, [sectionUrl, sectionDetails]) => {
        const { forms, sectionName } = sectionDetails;

        let updatedSectionName;

        if (sectionName === "Job requirements") {
          updatedSectionName = `Section 3: ${sectionName}`;
        } else if (sectionName === "Onboarding") {
          updatedSectionName = `Section 4: ${sectionName}`;
        } else {
          updatedSectionName = sectionName;
        }

        const totalSteps = forms.length;
        const totalStepsCompleted = forms.filter(
          (form) => form.completed
        ).length;

        acc[updatedSectionName] = {
          steps: forms.reduce((stepsAcc, form) => {
            return {
              ...stepsAcc,
              [form.name]: {
                completed: form.completed,
                url: `/profile/edit/job/${jobId}/${sectionUrl}/${form.url}`,
                firstNonCompletedStep: false,
              },
            };
          }, {}),
          totalSteps,
          totalStepsCompleted,
        };

        return { ...acc };
      },
      {}
    );

  const registrationState = buildRegistrationState(appData);
  const { "Section 1: Registration": registration } = registrationState;

  const legalComplianceState = buildLegalComplianceState(appData);
  const { "Section 2: Legal compliance": legalCompliance } =
    legalComplianceState;

  const restOnboardingState = buildCustomOnboardingState();

  let currentJobProgressState = {
    ...registrationState,
    ...legalComplianceState,
    ...restOnboardingState,
  };

  const firstNonCompletedUrl = returnFirstNonCompletedStep(jobId, appData);

  if (firstNonCompletedUrl) {
    currentJobProgressState = updateFirstNonCompletedStep(
      currentJobProgressState,
      `/my-account${firstNonCompletedUrl}`
    );
  }

  const sortedOnboardingState = Object.fromEntries(
    Object.entries(currentJobProgressState).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB)
    )
  );

  const steps = [
    {
      urls: [
        personalDetails,
        profilePicture,
        typeOfWork,
        cv,
        workHistory,
        emergencyContact,
        demographics,
      ],
      content: <span>Registration</span>,
      completed: registration.totalSteps === registration.totalStepsCompleted,
    },
    {
      urls: [
        wtr48HrWaiver,
        healthAndDisability,
        contractForServices,
        rtw,
        yotiSuccess,
        yotiError,
      ],
      content: <span>Legal compliance</span>,
      completed:
        legalCompliance.totalSteps === legalCompliance.totalStepsCompleted,
    },
    ...renderSectionsWithForms(currentJob.sections, jobId),
  ];

  const getCurrentIndex = () => {
    const currentPath = window.location.pathname;
    return steps.findIndex((step) => step.urls.includes(currentPath));
  };

  const navigateTo = (url) => history.push(url.replace(/^\/my-account\//, "/"));

  return (
    <div>
      <div className="container pb-4">
        <div className="row">
          <div className="col-12">
            <ul
              className={`list-unstyled p-0 d-flex ${style.candidateJob__steps}`}
            >
              {steps.map(({ urls, content }, index) => (
                <li
                  key={`${index}_onboardState_item `}
                  className={`onboardState_item ${
                    steps[index].completed ? "cursor-pointer" : ""
                  }`}
                  onClick={
                    steps[index].completed
                      ? () => {
                          navigateTo(urls[0]);
                        }
                      : null
                  }
                >
                  <OnboardStateItem
                    isCompleted={steps[index].completed}
                    isActive={getCurrentIndex() === index}
                    index={index}
                    currentIndex={getCurrentIndex()}
                  >
                    {content}
                  </OnboardStateItem>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="row bg-white position-relative overflow-hidden mt-2">
          <div className="col-md-12">
            <div className={style.candidateJob__stepContainer}>
              {cloneElement(children, {
                currentJobProgressState: sortedOnboardingState,
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ core }) => ({
  appData: core.app,
});
const mapDispatchToProps = {};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const withApi = compose(withRedux, withRouter);

export default withApi(CandidateJobContainer);
